import { usePrequelContext } from '../../../contexts';
import { DESTINATIONS_LIST } from '../../../constants/prequel';
import { useDestinationForm, VendorName } from '../../../contexts/@prequel';
import { useDataSyncTranslations } from '../../../hooks/useDataSyncTranslations';
import { GhostButton, PrimaryButton, SecondaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText, TextArea } from 'ui/inputs';
import { RadioGroup } from 'ui/radioGroup/radioGroup';
import { BasicSelect, BaseOption } from 'ui/selects';
import { Toggle, TOGGLE_SIZES } from 'ui/toggle';
import { useState, useMemo, useRef, Fragment, useCallback, useEffect } from 'react';
import { Orientation } from 'ui/radioGroup/types';
import { Link } from 'ui/links';
import { BUTTON_SIZES } from 'ui/button/constants';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { MakeCopy16 } from 'ui/icons/aurora/16px/MakeCopy16';
import { Confirm16 } from 'ui/icons/aurora/16px/Confirm16';
import { Tooltip } from 'ui/tooltip/Tooltip';
import {
  CreateOrEditActionFooter,
  IconWrapper,
  StyledInputText,
  CreateOrEditActionLayout,
  PageContainer,
  StyledPageHeader,
  FormContainer,
  DescriptionContainer,
} from './DestinationFormPage.styles';
import { parseMissingFields } from './formValidationUtils';
import { DataSyncLoadingState } from '../common/DataSyncStates';

export type DestinationPageMode = 'create' | 'edit';

export interface DestinationPageProps {
  mode: DestinationPageMode;
  destinationId?: string; // Optional for edit mode
}

export const DestinationFormPage = ({ mode, destinationId }: DestinationPageProps) => {
  const { selectedDestination, navigateToIndex, getVendorByKey } = usePrequelContext();
  const vendorInformation = getVendorByKey(selectedDestination || '');
  const { t } = useDataSyncTranslations();
  const [isSaving, setIsSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Copy functionality state
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>();

  // Get the vendor name from the selected destination
  const destinationKey = mode === 'create' ? selectedDestination : destinationId;
  const selectedDestinationData =
    DESTINATIONS_LIST[destinationKey as keyof typeof DESTINATIONS_LIST];

  // Use the useDestinationForm hook to get form data - MUST be called before any conditional returns
  const { formSections, destination, setDestination, testConnection, getPreparedDestination } =
    useDestinationForm((selectedDestinationData?.id || 'bigquery') as VendorName);

  // Filter out the "Select destination vendor" section
  const filteredFormSections = useMemo(() => {
    return formSections.filter((section) => section.title !== 'Select destination vendor');
  }, [formSections]);

  // Parse test connection errors to highlight missing fields
  const [testConnectionErrors, setTestConnectionErrors] = useState<string[]>([]);
  const [testConnectionErrorDetails, setTestConnectionErrorDetails] = useState<
    Record<string, string>
  >({});

  // Test connection state
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  // Helper function to handle test connection errors
  const handleTestConnectionError = useCallback(
    (errorMessage: string) => {
      if (!errorMessage || errorMessage === '') {
        setTestConnectionErrors([]);
        setTestConnectionErrorDetails({});
        return;
      }

      const { fields, errorDetails } = parseMissingFields(errorMessage, filteredFormSections);
      setTestConnectionErrors(fields);
      setTestConnectionErrorDetails(errorDetails);
    },
    [filteredFormSections],
  );

  async function testDestinationConnection() {
    setTestRunning(true);
    setTestResult('TEMP:: Testing new connection...');

    // Clear any previous errors when starting a new test
    handleTestConnectionError('');

    try {
      const result = await testConnection();
      if (result.status === 'success') {
        setTestResult('Connection test successful.');
        // Clear any previous errors when test succeeds
        handleTestConnectionError('');
      } else {
        setTestResult(result.message || 'Connection test failed.');
        // Pass error message to parent for parsing missing fields
        handleTestConnectionError(result.message || 'Connection test failed.');
      }
    } catch (error) {
      const errorMessage = 'Connection test failed with an error.';
      setTestResult(errorMessage);
      handleTestConnectionError(errorMessage);
    } finally {
      setTestRunning(false);
    }
  }

  // Validation function to check if all required fields are filled
  const validateRequiredFields = useCallback(() => {
    const errors: string[] = [];

    // Check destination name (always required)
    if (!destination.name || destination.name.toString().trim() === '') {
      errors.push('Destination name is required');
    }

    // Check all required fields in form sections
    filteredFormSections.forEach((section) => {
      section.fields?.forEach((field) => {
        if (field.required) {
          const fieldValue = (destination as any)[field.name];

          // Skip const fields as they are pre-filled
          if (field.const) {
            return;
          }

          // Check if field is empty or undefined
          if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
            errors.push(`${field.label} is required`);
          }

          // For string values, check if they're just whitespace
          if (typeof fieldValue === 'string' && fieldValue.trim() === '') {
            errors.push(`${field.label} is required`);
          }
        }
      });
    });

    return errors;
  }, [destination, filteredFormSections]);

  const preparedDestination = useMemo(() => getPreparedDestination(), [getPreparedDestination]);

  // Get validation errors for display
  const validationErrors = useMemo(() => {
    return validateRequiredFields();
  }, [validateRequiredFields]);

  // Enhanced getFieldError function that includes test connection errors
  const getFieldError = useCallback(
    (fieldName: string) => {
      // Check for test connection errors first
      if (testConnectionErrors.includes(fieldName)) {
        // Find the field to get its label for better error message
        let fieldLabel = fieldName;

        // Check destination name field
        if (fieldName === 'name') {
          fieldLabel = 'Destination name';
        } else {
          // Check form sections for field label
          for (const section of filteredFormSections) {
            const field = section.fields?.find((f) => f.name === fieldName);
            if (field) {
              fieldLabel = field.label;
              break;
            }
          }
        }

        // Check if this is a validation pattern error by looking at the original error message
        // We need to access the original error message to provide more specific feedback
        const originalErrorMessage = testConnectionErrorDetails[fieldName];

        // For now, provide a generic validation error message
        // In a more sophisticated implementation, you could parse the specific validation error
        return originalErrorMessage || `${fieldLabel} is required for connection`;
      }

      // Only show validation errors after submit is attempted
      if (!showValidation) {
        return null;
      }

      const fieldValue = (destination as any)[fieldName];

      // Check if field is required and empty
      if (fieldName === 'name') {
        if (!fieldValue || fieldValue.toString().trim() === '') {
          return 'Destination name is required';
        }
      }

      // Check form sections for field errors
      for (const section of filteredFormSections) {
        const field = section.fields?.find((f) => f.name === fieldName);
        if (field?.required) {
          // Skip const fields as they are pre-filled
          if (field.const) {
            continue;
          }

          // Check if field is empty or undefined
          if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
            return `${field.label} is required`;
          }

          // For string values, check if they're just whitespace
          if (typeof fieldValue === 'string' && fieldValue.trim() === '') {
            return `${field.label} is required`;
          }
        }
      }

      return null;
    },
    [
      destination,
      filteredFormSections,
      showValidation,
      testConnectionErrors,
      testConnectionErrorDetails,
    ],
  );

  // Show 'copied' indicator, then revert after 3 seconds
  const onCopy = useCallback(() => {
    if (copiedTimer.current) {
      clearTimeout(copiedTimer.current);
    }
    setIsCopied(true);
    copiedTimer.current = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimer.current) {
        clearTimeout(copiedTimer.current);
      }
    };
  }, []);

  // TODO: Remove this once we have a way to get the recipient_id and enabled_models from the destination
  useEffect(() => {
    setDestination({ recipient_id: '21f9ad4b-c37f-4b5a-bb30-58b459acf58d' });
    setDestination({ enabled_models: ['*'] });
  }, []);

  // Initialize radio field values when form sections are loaded
  useEffect(() => {
    if (filteredFormSections.length > 0) {
      const radioFieldUpdates: Record<string, any> = {};

      filteredFormSections.forEach((section) => {
        section.fields?.forEach((field) => {
          if (field.form_element === 'radio' && field.enum && field.enum.length > 0) {
            const currentValue = (destination as any)[field.name];
            const enumValues = field.enum.map((opt: any) => String(opt.key));

            // If the field doesn't have a value or the value is not in the enum options, set it to the first option
            if (
              currentValue === undefined ||
              currentValue === null ||
              !enumValues.includes(String(currentValue))
            ) {
              radioFieldUpdates[field.name] = String(field.enum[0].key);
            }
          }
        });
      });

      // Apply all radio field updates at once
      if (Object.keys(radioFieldUpdates).length > 0) {
        setDestination(radioFieldUpdates);
      }
    }
  }, [filteredFormSections, destination, setDestination]);

  if (formSections.length === 0) {
    return <DataSyncLoadingState />;
  }

  const handleBack = () => {
    navigateToIndex();
  };

  const handleSave = async () => {
    // Trigger validation on submit
    setShowValidation(true);

    validateRequiredFields();

    setIsSaving(true);
    try {
      if (mode === 'create') {
        // TODO: Implement destination creation logic using preparedDestination
        console.log('Creating destination:', preparedDestination);
      } else {
        // TODO: Implement destination update logic
        console.log('Updating destination:', destinationId, preparedDestination);
      }

      // Navigate back to index after save
      // navigateToIndex();
    } catch (error) {
      console.error('Error saving destination:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const pageTitle =
    mode === 'create'
      ? `${t('createDestinationModal.new')} ${selectedDestinationData.name} ${t('createDestinationModal.destination')}`
      : `${t('createDestinationModal.edit')} ${selectedDestinationData.name} ${t('createDestinationModal.destination')}`;

  return (
    <>
      <StyledPageHeader
        pageActions={null}
        breadcrumb={{
          href: '/integrations/dataSync',
          content: <div onClick={handleBack}>{t('createDestinationModal.back')}</div>,
        }}
      >
        {pageTitle}
      </StyledPageHeader>

      <PageContainer data-test="destination-form-page">
        <DescriptionContainer>
          <div>
            {t('createDestinationModal.description', {
              destination: selectedDestinationData.name,
            })}
          </div>
          <div>
            {t('createDestinationModal.descriptionPart2', {
              destination: selectedDestinationData.name,
            })}{' '}
            <Link href={vendorInformation?.docs || ''} target="_blank">
              {t('createDestinationModal.setupGuide')}
            </Link>{' '}
            {t('createDestinationModal.descriptionPart3', {
              destination: selectedDestinationData.name,
            })}
          </div>
        </DescriptionContainer>

        <FormContainer>
          <FormField
            key={'name'}
            labelText={'Destination name'}
            {...(getFieldError('name') && {
              hasError: true,
              errorText: getFieldError('name'),
            })}
          >
            <InputText
              id={'name'}
              type={'text'}
              placeholder={''}
              value={destination['name']?.toString() || ''}
              onChange={(value) => {
                setDestination({ ['name']: value });
              }}
            />
          </FormField>

          {filteredFormSections.map((section, sectionIndex) => (
            <Fragment key={section.id || sectionIndex}>
              {/* Fields */}
              {section.fields?.map((field, fieldIndex) => {
                // Handle const fields
                if (field.const) {
                  const fieldValue =
                    typeof field.const === 'object'
                      ? JSON.stringify(field.const, null, 2)
                      : field.const.toString();

                  return (
                    <FormField key={field.name || fieldIndex} labelText={field.label}>
                      <StyledInputText
                        id={field.name}
                        type="text"
                        value={fieldValue}
                        isDisabled={true}
                        Suffix={
                          <Tooltip
                            tooltipContent={isCopied ? 'Copied!' : 'Copy to clipboard'}
                            open={isCopied || undefined}
                          >
                            <CopyToClipboard text={fieldValue} onCopy={onCopy}>
                              <IconWrapper>
                                {isCopied ? (
                                  <Confirm16 className="shape" isColorInherited />
                                ) : (
                                  <MakeCopy16 />
                                )}
                              </IconWrapper>
                            </CopyToClipboard>
                          </Tooltip>
                        }
                      />
                    </FormField>
                  );
                }

                // Handle select fields
                if (field.form_element === 'select') {
                  const items: BaseOption<string>[] =
                    field.enum?.map(({ key, display }) => ({
                      id: key.toString(),
                      value: key.toString(),
                      label: display,
                    })) || [];

                  const selectedValue = (destination as any)[field.name];

                  return (
                    <FormField
                      key={field.name || fieldIndex}
                      labelText={field.label}
                      hasError={!!getFieldError(field.name)}
                      errorText={getFieldError(field.name) || ''}
                    >
                      <BasicSelect
                        id={field.name}
                        options={items}
                        defaultValues={selectedValue ? [selectedValue.toString()] : []}
                        onSelect={([value]) => {
                          setDestination({ [field.name]: value });
                        }}
                        placeholder="Select an option"
                      />
                    </FormField>
                  );
                }

                // Handle input fields
                if (field.form_element === 'input') {
                  return (
                    <FormField
                      key={field.name || fieldIndex}
                      labelText={field.label}
                      hasError={!!getFieldError(field.name)}
                      errorText={getFieldError(field.name) || ''}
                    >
                      <InputText
                        id={field.name}
                        type={field.input_type || 'text'}
                        placeholder={field.placeholder}
                        value={(destination as any)[field.name]?.toString() || ''}
                        onChange={(value) => {
                          setDestination({ [field.name]: value });
                        }}
                      />
                    </FormField>
                  );
                }

                // Handle textarea fields
                if (field.form_element === 'textarea') {
                  return (
                    <FormField
                      key={field.name || fieldIndex}
                      labelText={field.label}
                      hasError={!!getFieldError(field.name)}
                      errorText={getFieldError(field.name) || ''}
                    >
                      <TextArea
                        id={field.name}
                        placeholder={field.placeholder}
                        value={(destination as any)[field.name]?.toString() || ''}
                        onChange={(value) => {
                          setDestination({ [field.name]: value });
                        }}
                      />
                    </FormField>
                  );
                }

                // Handle radio fields
                if (field.form_element === 'radio') {
                  if (field.type === 'boolean') {
                    return (
                      <FormField
                        key={field.name || fieldIndex}
                        labelText={field.label ? field.label : field.name}
                        hasError={!!getFieldError(field.name)}
                        errorText={getFieldError(field.name) || ''}
                      >
                        <Toggle
                          data-test={`toggle-${field.name}-${(destination as any)[field.name]}`}
                          key={`${field.name}-${(destination as any)[field.name]}`}
                          id={field.name}
                          name={field.name}
                          isActive={(destination as any)[field.name] === true}
                          onClick={(isActive?: boolean) => {
                            setDestination({ [field.name]: isActive });
                          }}
                          size={TOGGLE_SIZES.LARGE}
                          aria-required={field.required}
                        />
                      </FormField>
                    );
                  }

                  // Convert enum options to RadioGroup format
                  const radioOptions =
                    field.enum?.map((option: any) => ({
                      id: String(option.key),
                      value: String(option.key),
                      label: option.display,
                      isDisabled: option.disabled,
                    })) || [];

                  // Get current value with fallback to first enum option
                  const currentValue = (destination as any)[field.name];
                  const enumValues = field.enum?.map((opt: any) => String(opt.key)) || [];
                  const defaultValue = enumValues.includes(String(currentValue))
                    ? String(currentValue)
                    : field.enum && field.enum.length > 0
                      ? String(field.enum[0].key)
                      : '';

                  return (
                    <FormField
                      key={field.name || fieldIndex}
                      labelText={field.label ? field.label : field.name}
                      hasError={!!getFieldError(field.name)}
                      errorText={getFieldError(field.name) || ''}
                    >
                      <RadioGroup
                        data-test={`toggle-${field.name}-${(destination as any)[field.name]}`}
                        key={`${field.name}-${defaultValue}`}
                        name={field.name}
                        options={radioOptions}
                        value={defaultValue}
                        onSelect={(value: string) => {
                          setDestination({ [field.name]: value });
                        }}
                        isBordered={true}
                        orientation={Orientation.HORIZONTAL}
                        aria-required={field.required}
                      />
                    </FormField>
                  );
                }
                return null;
              })}
            </Fragment>
          ))}
        </FormContainer>

        {/* Temp Test Connection Component*/}
        <div
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '24px',
            backgroundColor: '#fafafa',
            marginTop: '24px',
          }}
        >
          <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
            Temporary Test Connection
          </h4>
          {testResult && (
            <p
              style={{
                margin: '0 0 16px 0',
                fontSize: '14px',
                color: testResult.includes('successful') ? '#4caf50' : '#f44336',
                fontWeight: '500',
              }}
            >
              {testResult}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <SecondaryButton
              onClick={testDestinationConnection}
              isDisabled={testRunning}
              data-test="medium-secondary-button"
            >
              Test Connection
            </SecondaryButton>
          </div>{' '}
          {/* </div> */}
        </div>
      </PageContainer>

      <CreateOrEditActionFooter>
        <CreateOrEditActionLayout>
          <GhostButton onClick={handleBack} size={BUTTON_SIZES.LARGE}>
            {t('createDestinationModal.cancel')}
          </GhostButton>
          <PrimaryButton
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={isSaving || (showValidation && validationErrors.length > 0)}
          >
            {t('createDestinationModal.createButton')}
          </PrimaryButton>
        </CreateOrEditActionLayout>
      </CreateOrEditActionFooter>
    </>
  );
};
